<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-xl font-semibold">Manuals Management</h2>
      <button
        @click="showUploadForm = !showUploadForm"
        class="bg-custom-yellow hover:bg-orange-600 text-white font-bold py-2 px-4 rounded flex items-center"
      >
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        {{ showUploadForm ? 'Cancel' : 'Upload Manual' }}
      </button>
    </div>

    <!-- Upload Form -->
    <div v-if="showUploadForm" class="bg-white border border-gray-200 rounded-lg p-6 mb-6">
      <h3 class="text-lg font-semibold mb-4">Upload New Manual</h3>
      <form @submit.prevent="uploadManual" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Title *</label>
          <input
            v-model="uploadForm.title"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            placeholder="Enter manual title"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Description *</label>
          <textarea
            v-model="uploadForm.description"
            required
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            placeholder="Enter manual description"
          ></textarea>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">File *</label>
          <input
            ref="fileInput"
            type="file"
            accept=".pdf,.ppt,.pptx,.doc,.docx"
            @change="handleFileSelect"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-yellow"
          />
          <p class="text-sm text-gray-500 mt-1">Supported formats: PDF, PPT, PPTX, DOC, DOCX</p>
        </div>

        <div class="flex justify-end space-x-3">
          <button
            type="button"
            @click="cancelUpload"
            class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="uploading"
            class="px-4 py-2 bg-custom-yellow text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ uploading ? 'Uploading...' : 'Upload Manual' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Manuals List -->
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold">Uploaded Manuals</h3>
      </div>

      <div v-if="loading" class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-yellow"></div>
      </div>

      <div v-else-if="manuals.length === 0" class="text-center py-8 text-gray-500">
        No manuals uploaded yet.
      </div>

      <div v-else class="divide-y divide-gray-200">
        <div
          v-for="manual in manuals"
          :key="manual.id"
          class="px-6 py-4 hover:bg-gray-50"
        >
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <h4 class="text-lg font-medium text-gray-900">{{ manual.title }}</h4>
              <p class="text-sm text-gray-600 mt-1">{{ manual.description }}</p>
              <div class="flex items-center text-sm text-gray-500 mt-2">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                {{ formatDate(manual.created_at) }}
                <span v-if="manual.creator" class="ml-4">
                  • Uploaded by {{ manual.creator.first_name }} {{ manual.creator.last_name }}
                </span>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <a
                v-if="manual.file_url"
                :href="manual.file_url"
                target="_blank"
                class="text-custom-blue hover:text-blue-700 px-3 py-1 rounded-md text-sm font-medium"
              >
                View
              </a>
              <button
                @click="deleteManual(manual.id)"
                class="text-red-600 hover:text-red-800 px-3 py-1 rounded-md text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import AWS from 'aws-sdk';

export default {
  name: "ManualsManagementTab",
  data() {
    return {
      manuals: [],
      loading: false,
      showUploadForm: false,
      uploading: false,
      uploadForm: {
        title: '',
        description: '',
        file: null
      }
    };
  },
  mounted() {
    this.fetchManuals();
  },
  methods: {
    async fetchManuals() {
      this.loading = true;
      try {
        const response = await api.getManuals();
        this.manuals = response.data || response;
      } catch (error) {
        console.error('Error fetching manuals:', error);
        // Fallback to empty array if API fails
        this.manuals = [];
        if (this.$toast) {
          this.$toast.error('Failed to load manuals');
        }
      } finally {
        this.loading = false;
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadForm.file = file;
      }
    },

    async uploadManual() {
      if (!this.uploadForm.file) {
        this.$toast.error('Please select a file');
        return;
      }

      this.uploading = true;
      try {
        // Upload file to DigitalOcean Spaces
        const fileUrl = await this.uploadToSpaces(this.uploadForm.file);

        // Create manual record
        const manualData = {
          title: this.uploadForm.title,
          description: this.uploadForm.description,
          file_url: fileUrl
        };

        await api.createManual(manualData);

        if (this.$toast) {
          this.$toast.success('Manual uploaded successfully');
        }
        this.cancelUpload();
        this.fetchManuals();
      } catch (error) {
        console.error('Error uploading manual:', error);
        if (this.$toast) {
          this.$toast.error('Failed to upload manual');
        }
      } finally {
        this.uploading = false;
      }
    },

    async uploadToSpaces(file) {
      // Configure AWS SDK for DigitalOcean Spaces
      const s3 = new AWS.S3({
        endpoint: new AWS.Endpoint('https://blr1.digitaloceanspaces.com'),
        signatureVersion: 'v4',
        accessKeyId: 'DO00PK2KLQ4HUBKTB6R3',
        secretAccessKey: 'L3yOKmH3Dqfpc3tVZv4Mz5O+jd1bXTobKS//zmY72Rk',
        region: 'blr1',
      });

      const fileName = `manuals/${Date.now()}-${file.name}`;

      const params = {
        Bucket: 'jiji-bucket',
        Key: fileName,
        Body: file,
        ACL: 'public-read',
        ContentType: file.type,
      };

      const result = await s3.upload(params).promise();
      return result.Location;
    },

    cancelUpload() {
      this.showUploadForm = false;
      this.uploadForm = {
        title: '',
        description: '',
        file: null
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async deleteManual(id) {
      if (!confirm('Are you sure you want to delete this manual?')) {
        return;
      }

      try {
        await api.deleteManual(id);
        if (this.$toast) {
          this.$toast.success('Manual deleted successfully');
        }
        this.fetchManuals();
      } catch (error) {
        console.error('Error deleting manual:', error);
        if (this.$toast) {
          this.$toast.error('Failed to delete manual');
        }
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
/* Additional styles if needed */
</style>